import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { LOGO_URL, TOURNAMENT } from "../lib/constants";
import { useT } from "../lib/i18n";
import LanguageSwitcher from "./LanguageSwitcher";

function DropdownNav({ label, items, testid }) {
    const [open, setOpen] = useState(false);
    const loc = useLocation();
    const ref = useRef(null);
    const isActive = items.some((c) => loc.pathname.startsWith(c.to));

    useEffect(() => { setOpen(false); }, [loc.pathname]);
    useEffect(() => {
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    return (
        <div
            ref={ref}
            className="relative"
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            data-testid={testid}
        >
            <button
                onClick={() => setOpen((v) => !v)}
                className={`px-3 py-2 font-display text-sm tracking-[0.18em] uppercase transition-colors link-underline flex items-center gap-1 ${isActive ? "text-[#7FD93C]" : "text-white/80 hover:text-white"}`}
                aria-expanded={open}
                data-testid={`${testid}-btn`}
            >
                {label}
                <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.15 }}
                        className="absolute left-1/2 -translate-x-1/2 top-full pt-2 min-w-[210px] z-50"
                        data-testid={`${testid}-dropdown`}
                    >
                        <div className="bg-black/95 backdrop-blur-xl border border-white/10 shadow-xl angle-tag overflow-hidden">
                            {items.map((c) => (
                                <NavLink
                                    key={c.to}
                                    to={c.to}
                                    data-testid={`nav-${c.to.slice(1)}`}
                                    className={({ isActive: a }) =>
                                        `block px-5 py-3 font-display text-sm tracking-[0.18em] uppercase border-l-2 transition-colors ${a ? "border-[#7FD93C] text-[#7FD93C] bg-white/5" : c.highlight ? "border-transparent text-[#7FD93C] hover:bg-[#7FD93C]/10" : "border-transparent text-white/80 hover:text-[#4FE0D5] hover:bg-white/5"}`
                                    }
                                >
                                    {c.label}
                                </NavLink>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

function MobileDropdown({ label, items, testid }) {
    const [open, setOpen] = useState(false);
    const loc = useLocation();
    const childActive = items.some((c) => loc.pathname.startsWith(c.to));
    return (
        <div data-testid={`mobile-${testid}`}>
            <button
                onClick={() => setOpen((v) => !v)}
                className={`w-full flex items-center justify-between px-3 py-3 font-display text-lg tracking-wider uppercase border-l-2 ${childActive ? "border-[#7FD93C] text-[#7FD93C] bg-white/5" : "border-transparent text-white/80"}`}
                data-testid={`mobile-${testid}-btn`}
            >
                <span>{label}</span>
                <ChevronDown size={18} className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
                {open && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                        {items.map((c) => (
                            <NavLink
                                key={c.to}
                                to={c.to}
                                data-testid={`mobile-nav-${c.to.slice(1)}`}
                                className={({ isActive }) =>
                                    `block pl-8 pr-3 py-2.5 font-display text-base tracking-wider uppercase border-l-2 ${isActive ? "border-[#7FD93C] text-[#7FD93C] bg-white/5" : c.highlight ? "border-transparent text-[#7FD93C]" : "border-transparent text-white/70"}`
                                }
                            >
                                {c.label}
                            </NavLink>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default function Header() {
    const [open, setOpen] = useState(false);
    const loc = useLocation();
    const { t } = useT();

    const INFOS_CHILDREN = [
        { to: "/infos", label: t("nav.infos_practical") },
        { to: "/inscription", label: t("nav.inscription"), highlight: true },
        { to: "/equipes", label: t("nav.teams") },
        { to: "/reglements", label: t("nav.rules") },
        { to: "/partenaires", label: t("nav.partners") },
        { to: "/contact", label: t("nav.contact") },
    ];

    const STATS_CHILDREN = [
        { to: "/classement", label: t("nav.standings") },
        { to: "/pointeurs", label: t("nav.scorers") },
        { to: "/eliminatoires", label: t("nav.playoffs") },
    ];

    const NAV = [
        { type: "dropdown", testid: "nav-infos", label: t("nav.infos"), children: INFOS_CHILDREN },
        { type: "link", to: "/horaire", label: t("nav.schedule") },
        { type: "dropdown", testid: "nav-statistiques", label: t("nav.stats"), children: STATS_CHILDREN },
        { type: "link", to: "/nouvelles", label: t("nav.news") },
        { type: "link", to: "/galerie", label: t("nav.gallery") },
    ];

    React.useEffect(() => { setOpen(false); }, [loc.pathname]);

    return (
        <header className="sticky top-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/10" data-testid="site-header">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-20">
                    <Link to="/" data-testid="link-home-logo" className="flex items-center gap-3">
                        <img src={LOGO_URL} alt="Défi Ultime" className="h-12 w-12 object-contain glow-pulse" />
                        <div className="hidden sm:block">
                            <div className="font-display text-2xl tracking-wider leading-none">{TOURNAMENT.name}</div>
                            <div className="text-[10px] tracking-[0.3em] text-[#4FE0D5] uppercase">{t("tournament.edition")}</div>
                        </div>
                    </Link>
                    <nav className="hidden lg:flex items-center gap-1">
                        {NAV.map((n) => n.type === "dropdown"
                            ? <DropdownNav key={n.testid} label={n.label} items={n.children} testid={n.testid} />
                            : (
                                <NavLink
                                    key={n.to}
                                    to={n.to}
                                    data-testid={`nav-${n.to.slice(1)}`}
                                    className={({ isActive }) =>
                                        `px-3 py-2 font-display text-sm tracking-[0.18em] uppercase transition-colors link-underline ${isActive ? "text-[#7FD93C]" : "text-white/80 hover:text-white"}`
                                    }
                                >
                                    {n.label}
                                </NavLink>
                            ))}
                    </nav>
                    <div className="flex items-center gap-3">
                        <div className="hidden md:block"><LanguageSwitcher variant="header" /></div>
                        <Link to="/admin/login" data-testid="link-admin" className="hidden md:inline-flex btn-ghost">
                            {t("nav.admin")}
                        </Link>
                        <button
                            data-testid="btn-menu-toggle"
                            className="lg:hidden text-white p-2"
                            onClick={() => setOpen((v) => !v)}
                            aria-label={t("nav.menu")}
                        >
                            {open ? <X size={28} /> : <Menu size={28} />}
                        </button>
                    </div>
                </div>
            </div>
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="lg:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl overflow-hidden"
                        data-testid="mobile-menu"
                    >
                        <div className="px-4 py-4 grid gap-1">
                            <div className="flex justify-between items-center pb-2 mb-1 border-b border-white/10">
                                <span className="label-mini">{t("footer.lang")}</span>
                                <LanguageSwitcher variant="header" />
                            </div>
                            {NAV.map((n) => n.type === "dropdown"
                                ? <MobileDropdown key={n.testid} label={n.label} items={n.children} testid={n.testid} />
                                : (
                                    <NavLink
                                        key={n.to}
                                        to={n.to}
                                        data-testid={`mobile-nav-${n.to.slice(1)}`}
                                        className={({ isActive }) =>
                                            `block px-3 py-3 font-display text-lg tracking-wider uppercase border-l-2 ${isActive ? "border-[#7FD93C] text-[#7FD93C] bg-white/5" : "border-transparent text-white/80"}`
                                        }
                                    >
                                        {n.label}
                                    </NavLink>
                                ))}
                            <Link to="/admin/login" data-testid="mobile-link-admin" className="px-3 py-3 font-display text-lg tracking-wider uppercase text-[#4FE0D5]">{t("nav.admin")}</Link>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
